import {
  computed,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { ICompleteOnboardingDto, IOnboardingStatus } from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { OnboardingGateway } from './onboarding.gateway';

@Injectable({
  providedIn: 'root',
})
export class OnboardingStore {
  private readonly gateway: OnboardingGateway = new OnboardingGateway();
  private readonly statusState: WritableSignal<IOnboardingStatus | undefined> =
    signal<IOnboardingStatus | undefined>(undefined);
  private readonly loadingState: WritableSignal<boolean> = signal(false);
  private loadPromise: Promise<IOnboardingStatus | undefined> | undefined;

  readonly status: Signal<IOnboardingStatus | undefined> =
    this.statusState.asReadonly();

  readonly isLoading: Signal<boolean> = this.loadingState.asReadonly();

  readonly isCompleted: Signal<boolean> = computed(
    (): boolean => this.status()?.completed ?? false
  );

  load(): Promise<IOnboardingStatus | undefined> {
    if (this.statusState() !== undefined) {
      return Promise.resolve(this.statusState());
    }

    return this.fetchStatus();
  }

  async complete(
    query: ICompleteOnboardingDto
  ): Promise<IOnboardingStatus | undefined> {
    this.loadingState.set(true);

    try {
      const response: IApiResponse<IOnboardingStatus> = await firstValueFrom(
        this.gateway.save(query)
      );

      this.statusState.set(response.data);

      return response.data;
    } finally {
      this.loadingState.set(false);
    }
  }

  reload(): Promise<IOnboardingStatus | undefined> {
    return this.fetchStatus();
  }

  private fetchStatus(): Promise<IOnboardingStatus | undefined> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadingState.set(true);
    this.loadPromise = firstValueFrom(this.gateway.get())
      .then(
        (
          response: IApiResponse<IOnboardingStatus>
        ): IOnboardingStatus | undefined => {
          this.statusState.set(response.data);
          return response.data;
        }
      )
      .finally((): void => {
        this.loadingState.set(false);
        this.loadPromise = undefined;
      });

    return this.loadPromise;
  }
}
